import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Loader2, Trash2, Mail, Phone, Users, CalendarDays, Reply, Send, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const STATUS_TONE = {
  new: "bg-[#D98E73]/15 text-[#B5794E]",
  contacted: "bg-[#144F75]/10 text-[#144F75]",
  closed: "bg-[#F1EDE6] text-[#8FA89B]",
};
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return s; } };

function ReplyThread({ inquiry, onSent }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const replies = inquiry.replies || [];

  const send = async () => {
    if (msg.trim().length < 2) { toast.error("Write a reply first."); return; }
    setSending(true);
    try {
      const r = await api.post(`/admin/corporate-inquiries/${inquiry.id}/reply`, { message: msg });
      toast.success(r.data.emailed ? `Reply emailed to ${inquiry.email}` : "Reply saved — email not configured, send manually");
      setMsg("");
      setOpen(false);
      onSent();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-3">
      {replies.length > 0 && (
        <div className="space-y-2 mb-3" data-testid={`corp-replies-${inquiry.id}`}>
          {replies.map((r, i) => (
            <div key={i} className="flex gap-2.5 text-sm" data-testid={`corp-reply-${inquiry.id}-${i}`}>
              <CornerDownRight size={14} className="text-[#8FA89B] shrink-0 mt-1" />
              <div className="flex-1 bg-[#144F75]/[0.05] border border-[#144F75]/10 rounded-xl px-3.5 py-2.5">
                <p className="text-[#2A3942] leading-relaxed whitespace-pre-wrap">{r.message}</p>
                <p className="text-[11px] text-[#8FA89B] mt-1.5">{r.by} · {fmtDate(r.sent_at)}{r.emailed ? " · emailed" : " · not emailed"}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {open ? (
        <div className="space-y-2" data-testid={`corp-reply-form-${inquiry.id}`}>
          <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} autoFocus
            data-testid={`corp-reply-input-${inquiry.id}`} className="eyw-input w-full"
            placeholder={`Reply to ${inquiry.contact_name} — this is emailed to ${inquiry.email}…`} />
          <div className="flex items-center gap-2">
            <button onClick={send} disabled={sending} data-testid={`corp-reply-send-${inquiry.id}`} className="eyw-btn !py-2 text-sm disabled:opacity-60">
              {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} Send reply
            </button>
            <button onClick={() => setOpen(false)} data-testid={`corp-reply-cancel-${inquiry.id}`} className="eyw-btn-ghost !py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setOpen(true)} data-testid={`corp-reply-btn-${inquiry.id}`} className="eyw-btn-ghost !py-1.5 text-xs">
          <Reply size={13} /> {replies.length ? "Reply again" : "Reply"}
        </button>
      )}
    </div>
  );
}

export default function CorporateInquiriesPanel() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["corporate-inquiries", filter],
    queryFn: () => api.get("/admin/corporate-inquiries", { params: { status: filter } }).then((r) => r.data),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["corporate-inquiries"] });
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/corporate-inquiries/${id}/status?status=${status}`); toast.success(`Marked ${status}`); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/admin/corporate-inquiries/${id}`); toast.success("Inquiry removed"); refresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const list = data?.inquiries || [];

  return (
    <div data-testid="corporate-inquiries-panel">
      <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2 mb-1"><Building2 className="text-[#144F75]" /> Corporate Inquiries</h2>
      <p className="text-sm text-[#4A5D6B] mb-5">Requests from HR managers, team leads, and group organizers submitted via the homepage corporate section.</p>

      <div className="flex flex-wrap gap-2 mb-5">
        {[["", "All"], ["new", `New${data?.new_count ? ` (${data.new_count})` : ""}`], ["contacted", "Contacted"], ["closed", "Closed"]].map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`corp-filter-${v || "all"}`}
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${filter === v ? "bg-[#144F75] text-white" : "bg-white border border-[#E5E0D8] text-[#4A5D6B] hover:border-[#144F75]/40"}`}>{label}</button>
        ))}
      </div>

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : list.length === 0 ? (
        <p className="text-sm text-[#8FA89B]" data-testid="corp-empty">No corporate inquiries{filter ? ` with status "${filter}"` : " yet"}.</p>
      ) : (
        <div className="space-y-3">
          {list.map((q) => (
            <div key={q.id} data-testid={`corp-inquiry-${q.id}`} className="eyw-card p-5">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="font-serif text-lg text-[#1A242B]">{q.company}</span>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold ${STATUS_TONE[q.status] || STATUS_TONE.new}`} data-testid={`corp-status-${q.id}`}>{q.status}</span>
                <span className="text-xs text-[#8FA89B] ml-auto">{fmtDate(q.created_at)}</span>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#4A5D6B] mb-2.5">
                <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-[#8FA89B]" /> {q.contact_name} · <a href={`mailto:${q.email}`} className="text-[#144F75] hover:underline">{q.email}</a></span>
                {q.phone && <span className="inline-flex items-center gap-1.5" data-testid={`corp-phone-${q.id}`}><Phone size={13} className="text-[#8FA89B]" /> <a href={`tel:${q.phone}`} className="text-[#144F75] hover:underline">{q.phone}</a></span>}
                {q.group_size && <span className="inline-flex items-center gap-1.5"><Users size={13} className="text-[#8FA89B]" /> {q.group_size} people</span>}
                {q.timeframe && <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-[#8FA89B]" /> {q.timeframe}</span>}
              </div>
              <p className="text-sm text-[#2A3942] leading-relaxed bg-[#F9F7F3] rounded-xl px-4 py-3">{q.interest}</p>
              <ReplyThread inquiry={q} onSent={refresh} />
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-[#F1EDE6]">
                {q.status !== "contacted" && <button onClick={() => setStatus(q.id, "contacted")} data-testid={`corp-mark-contacted-${q.id}`} className="eyw-btn-ghost !py-1.5 text-xs">Mark contacted</button>}
                {q.status !== "closed" && <button onClick={() => setStatus(q.id, "closed")} data-testid={`corp-mark-closed-${q.id}`} className="eyw-btn-ghost !py-1.5 text-xs">Close</button>}
                {q.status !== "new" && <button onClick={() => setStatus(q.id, "new")} data-testid={`corp-mark-new-${q.id}`} className="eyw-btn-ghost !py-1.5 text-xs">Reopen</button>}
                <button onClick={() => remove(q.id)} data-testid={`corp-delete-${q.id}`} className="text-red-500 ml-auto p-1.5"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
